import { FiCalendar, FiPackage, FiUsers } from 'react-icons/fi'

import { numberToBRL } from '~/modules/sales/services'
import { LicenseExtensionPurchase } from '~/modules/sales/licenseExtensions/types'
import { getLocalDate } from '~/utils/getLocalDate'

type Props = {
  purchases: LicenseExtensionPurchase[]
}

export const PurchaseHistoryTable: React.FC<Props> = ({ purchases }) => {
  if (purchases.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-10 text-gpa-gray-800 gap-2'>
        <FiPackage size={36} />
        <p className='text-sm'>Nenhuma compra de licenças avulsas ainda.</p>
      </div>
    )
  }

  const totalLicenses = purchases.reduce((s, p) => s + p.licensesAmount, 0)
  const totalSpent = purchases.reduce((s, p) => s + p.proratedPrice, 0)

  return (
    <div className='flex flex-col gap-4'>
      {/* Summary strip */}
      <div className='grid grid-cols-3 gap-3'>
        <div className='bg-gpa-blue-50 rounded-xl p-3 flex flex-col items-center'>
          <span className='text-2xl font-bold text-gpa-blue-500'>
            {purchases.length}
          </span>
          <span className='text-xs text-gpa-gray-800 mt-0.5'>compras</span>
        </div>
        <div className='bg-gpa-blue-50 rounded-xl p-3 flex flex-col items-center'>
          <span className='text-2xl font-bold text-gpa-blue-500'>
            {totalLicenses}
          </span>
          <span className='text-xs text-gpa-gray-800 mt-0.5'>licenças adquiridas</span>
        </div>
        <div className='bg-gpa-blue-50 rounded-xl p-3 flex flex-col items-center'>
          <span className='text-2xl font-bold text-gpa-blue-500'>
            {numberToBRL(totalSpent / 100)}
          </span>
          <span className='text-xs text-gpa-gray-800 mt-0.5'>total investido</span>
        </div>
      </div>

      {/* Timeline rows */}
      <div className='flex flex-col gap-0 relative'>
        {/* vertical line */}
        <div className='absolute left-[19px] top-4 bottom-4 w-px bg-gpa-blue-50' />

        {purchases.map((p) => (
          <div key={p.id} className='flex gap-4 items-start py-3'>
            {/* dot */}
            <div className='flex-shrink-0 w-10 h-10 rounded-full bg-gpa-blue-50 border-2 border-gpa-blue-400 flex items-center justify-center z-10'>
              <FiPackage size={16} className='text-gpa-blue-500' />
            </div>

            {/* card */}
            <div className='flex-1 bg-white border border-gpa-gray-500 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div className='flex flex-col gap-1'>
                <span className='font-semibold text-sm'>
                  {p.licenseExtension.name}
                </span>
                <div className='flex items-center gap-3 text-xs text-gpa-gray-800'>
                  <span className='flex items-center gap-1'>
                    <FiUsers size={11} />
                    {p.licensesAmount} licenças
                  </span>
                  <span className='flex items-center gap-1'>
                    <FiCalendar size={11} />
                    {getLocalDate(p.createdAt)}
                  </span>
                </div>
              </div>

              <div className='flex sm:flex-col items-center sm:items-end gap-2'>
                <span className='text-base font-bold text-gpa-blue-500'>
                  {numberToBRL(p.proratedPrice / 100)}
                </span>
                <span className='text-xs text-gpa-gray-800 line-through'>
                  {numberToBRL(p.licenseExtension.price / 100)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
